/**
 * Shared auth-expiry signal for edge-function responses.
 *
 * Kept in its own module (no server-only imports) so client components can
 * recognize an expired session and prompt re-authentication without pulling
 * server code into the browser bundle.
 */

/**
 * `type` returned by `invokeEdgeFunction` when the user's session is missing or
 * expired. The UI should open the register/login modal on this signal rather
 * than surfacing the backend's raw "Not authenticated" string.
 */
export const AUTH_EXPIRED_TYPE = "auth_expired"

/** User-facing copy for an expired session. */
export const AUTH_EXPIRED_MESSAGE = "Your session has expired. Please sign in again."

/**
 * True when a `refreshSession()` failure is a dead/missing refresh token
 * (sign-in again), not a transient Auth outage.
 */
export function isInvalidRefreshTokenError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false
  const message =
    "message" in error && typeof error.message === "string"
      ? error.message.toLowerCase()
      : ""
  const code =
    "code" in error && typeof error.code === "string"
      ? error.code.toLowerCase()
      : ""
  return (
    code.includes("refresh_token") ||
    code === "invalid_grant" ||
    message.includes("refresh token") ||
    message.includes("invalid_grant") ||
    message.includes("invalid grant")
  )
}

/** True when a result/error carries the standardized auth-expiry `type`. */
export function isAuthExpiredResult(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    value.type === AUTH_EXPIRED_TYPE
  )
}
